import { Button, Collapse, Input, Select, Toggle } from '@appd/uik';
import { useEffect, useState } from 'react';
import { capitalizeFirstLetter } from '../../../../../utils';
import {
  LegendType,
  LegendTypeOption,
  StackingType,
  StackingTypeOption,
  TimeSeriesChartType,
  TimeSeriesChartTypeOption,
  TooltipType,
  TooltipTypeOption,
  YAxisType,
  YAxisTypeOption,
} from '../../../cartesian';
import { VisualizationPropertiesPanelProps } from '../../models';
import { getJsonataExpressionForVariable } from '../../utils';
import { ToggleSwitchWithTitle } from '../../components';
import { getStyles } from '../getStyles';

const { COLLAPSE_PANEL, PANEL, PROPERTY, PROPERTY_LABEL, TOGGLE_BUTTON } = getStyles();

export const TimeSeriesPropertiesPanel = ({
  props,
  updateProps,
  expressions,
}: VisualizationPropertiesPanelProps): JSX.Element => {
  const [data, setData] = useState<string>(props.data);
  const [xAxisMin, setXAxisMin] = useState<string | null>(props.xAxisMin);
  const [xAxisMax, setXAxisMax] = useState<string | null>(props.xAxisMax);
  const [chartType, setChartType] = useState<TimeSeriesChartTypeOption>(props.chartType);
  const [isXAxisTitleEnabled, setIsXAxisTitleEnabled] = useState<boolean>(props.xAxisTitle != null);
  const [xAxisTitle, setXAxisTitle] = useState<string>(props.xAxisTitle ?? '');
  const [xAxisTitleInput, setXAxisTitleInput] = useState<string>(xAxisTitle);
  const [isYAxisTitleEnabled, setIsYAxisTitleEnabled] = useState<boolean>(props.yAxisTitle != null);
  const [yAxisTitle, setYAxisTitle] = useState<string>(props.yAxisTitle ?? '');
  const [yAxisTitleInput, setYAxisTitleInput] = useState<string>(yAxisTitle);
  const [legendType, setLegendType] = useState<LegendTypeOption>(props.legendType);
  const [tooltipType, setTooltipType] = useState<TooltipTypeOption>(props.tooltipType);
  const [stackingType, setStackingType] = useState<StackingTypeOption>(props.stackingType);
  const [yAxisType, setYAxisType] = useState<YAxisTypeOption>(props.yAxisType);

  useEffect(() => {
    setData(props.data);
    setXAxisMin(props.xAxisMin);
    setXAxisMax(props.xAxisMax);
    setChartType(props.chartType);
    setIsXAxisTitleEnabled(props.xAxisTitle != null);
    setXAxisTitle(props.xAxisTitle ?? '');
    if (props.xAxisTitle != null) {
      setXAxisTitleInput(props.xAxisTitle ?? '');
    }
    setIsYAxisTitleEnabled(props.yAxisTitle != null);
    setYAxisTitle(props.yAxisTitle ?? '');
    if (props.yAxisTitle != null) {
      setYAxisTitleInput(props.yAxisTitle ?? '');
    }
    setLegendType(props.legendType);
    setTooltipType(props.tooltipType);
    setStackingType(props.stackingType);
    setYAxisType(props.yAxisType);
  }, [props]);

  return (
    <div key="timeSeriesChartProperties" className={PANEL as string}>
      <Collapse.Group open={[true]}>
        <Collapse title="Data binding">
          <Collapse.Panel className={COLLAPSE_PANEL}>
            <Select
              label="Main data set"
              value={[expressions.findIndex(variable => getJsonataExpressionForVariable(variable) === data)]}
              onChange={(value: number[]) => {
                const expressionVariable = expressions[value[0]];
                const expression = getJsonataExpressionForVariable(expressionVariable);
                setData(expression);
                updateProps({
                  data: expression,
                });
              }}
              showClear={false}
              required
            >
              {expressions.map(expression => (
                <Select.Option key={expression}>{expression}</Select.Option>
              ))}
            </Select>
            <Select
              label="Start time"
              value={[expressions.findIndex(variable => getJsonataExpressionForVariable(variable) === xAxisMin)]}
              onChange={(value: number[]) => {
                if (value.length === 0) {
                  updateProps({
                    xAxisMin: null,
                  });
                } else {
                  const expressionVariable = expressions[value[0]];
                  const expression = getJsonataExpressionForVariable(expressionVariable);
                  setXAxisMin(expression);
                  updateProps({
                    xAxisMin: expression,
                  });
                }
              }}
            >
              {expressions.map(expression => (
                <Select.Option key={expression}>{expression}</Select.Option>
              ))}
            </Select>
            <Select
              label="End time"
              value={[expressions.findIndex(variable => getJsonataExpressionForVariable(variable) === xAxisMax)]}
              onChange={(value: number[]) => {
                if (value.length === 0) {
                  updateProps({
                    xAxisMax: null,
                  });
                } else {
                  const expressionVariable = expressions[value[0]];
                  const expression = getJsonataExpressionForVariable(expressionVariable);
                  setXAxisMax(expression);
                  updateProps({
                    xAxisMax: expression,
                  });
                }
              }}
            >
              {expressions.map(expression => (
                <Select.Option key={expression}>{expression}</Select.Option>
              ))}
            </Select>
          </Collapse.Panel>
        </Collapse>
      </Collapse.Group>
      <Collapse.Group open={[true]}>
        <Collapse title="Visualization">
          <Collapse.Panel className={COLLAPSE_PANEL}>
            <div key="type" className={PROPERTY as string}>
              <div className={PROPERTY_LABEL as string}>Type</div>
              <Toggle
                selected={[Object.values(TimeSeriesChartType).findIndex(value => value === chartType)]}
                onSelectedChange={selected => {
                  const option = Object.values(TimeSeriesChartType)[selected[0]] as TimeSeriesChartTypeOption;
                  setChartType(option);
                  updateProps({
                    chartType: option,
                  });
                }}
              >
                {Object.values(TimeSeriesChartType).map(option => (
                  <Button key={option} className={TOGGLE_BUTTON}>
                    {capitalizeFirstLetter(option)}
                  </Button>
                ))}
              </Toggle>
            </div>
            <div key="xAxisTitle" className={PROPERTY as string}>
              <ToggleSwitchWithTitle
                title="X-axis title"
                key="isXAxisTitleEnabled"
                on={isXAxisTitleEnabled}
                onSwitch={on => {
                  setIsXAxisTitleEnabled(on);
                  updateProps({
                    xAxisTitle: on ? xAxisTitleInput ?? '' : null,
                  });
                }}
              />
              {isXAxisTitleEnabled && (
                <Input
                  key="xAxisTitleInput"
                  value={xAxisTitleInput}
                  onChange={e => {
                    setXAxisTitle(e.target.value);
                    setXAxisTitleInput(e.target.value);
                    updateProps({
                      xAxisTitle: e.target.value,
                    });
                  }}
                  required={true}
                />
              )}
            </div>
            <div key="yAxisTitle" className={PROPERTY as string}>
              <ToggleSwitchWithTitle
                title="Y-axis title"
                key="isYAxisTitleEnabled"
                on={isYAxisTitleEnabled}
                onSwitch={on => {
                  setIsYAxisTitleEnabled(on);
                  updateProps({
                    yAxisTitle: on ? yAxisTitleInput ?? '' : null,
                  });
                }}
              />
              {isYAxisTitleEnabled && (
                <Input
                  key="yAxisTitleInput"
                  value={yAxisTitleInput}
                  onChange={e => {
                    setYAxisTitle(e.target.value);
                    setYAxisTitleInput(e.target.value);
                    updateProps({
                      yAxisTitle: e.target.value,
                    });
                  }}
                  required={true}
                />
              )}
            </div>
            <div key="stacking" className={PROPERTY as string}>
              <div className={PROPERTY_LABEL as string}>Stacking</div>
              <Toggle
                selected={[Object.values(StackingType).findIndex(value => value === stackingType)]}
                onSelectedChange={selected => {
                  const option = Object.values(StackingType)[selected[0]] as StackingTypeOption;
                  setStackingType(option);
                  updateProps({
                    stackingType: option,
                  });
                }}
              >
                {Object.values(StackingType).map(option => (
                  <Button key={option} className={TOGGLE_BUTTON}>
                    {capitalizeFirstLetter(option)}
                  </Button>
                ))}
              </Toggle>
            </div>
            <div key="yAxisType" className={PROPERTY as string}>
              <div className={PROPERTY_LABEL as string}>Y-axis type</div>
              <Toggle
                selected={[Object.values(YAxisType).findIndex(value => value === yAxisType)]}
                onSelectedChange={selected => {
                  const option = Object.values(YAxisType)[selected[0]] as YAxisTypeOption;
                  setYAxisType(option);
                  updateProps({
                    yAxisType: option,
                  });
                }}
              >
                {Object.values(YAxisType).map(option => (
                  <Button key={option} className={TOGGLE_BUTTON}>
                    {capitalizeFirstLetter(option)}
                  </Button>
                ))}
              </Toggle>
            </div>
          </Collapse.Panel>
        </Collapse>
      </Collapse.Group>
      <Collapse.Group open={[true]}>
        <Collapse title="Legend">
          <Collapse.Panel className={COLLAPSE_PANEL}>
            <div key="layout" className={PROPERTY as string}>
              <div className={PROPERTY_LABEL as string}>Layout</div>
              <Toggle
                selected={[Object.values(LegendType).findIndex(value => value === legendType)]}
                onSelectedChange={selected => {
                  const option = Object.values(LegendType)[selected[0]] as LegendTypeOption;
                  setLegendType(option);
                  updateProps({
                    legendType: option,
                  });
                }}
              >
                {Object.values(LegendType).map(option => (
                  <Button key={option} className={TOGGLE_BUTTON}>
                    {capitalizeFirstLetter(option)}
                  </Button>
                ))}
              </Toggle>
            </div>
          </Collapse.Panel>
        </Collapse>
      </Collapse.Group>
      <Collapse.Group open={[true]}>
        <Collapse title="Tooltip">
          <Collapse.Panel className={COLLAPSE_PANEL}>
            <div key="configuration" className={PROPERTY as string}>
              <div className={PROPERTY_LABEL as string}>Configuration</div>
              <Toggle
                selected={[Object.values(TooltipType).findIndex(value => value === tooltipType)]}
                onSelectedChange={selected => {
                  const option = Object.values(TooltipType)[selected[0]] as TooltipTypeOption;
                  setTooltipType(option);
                  updateProps({
                    tooltipType: option,
                  });
                }}
              >
                {Object.values(TooltipType).map(option => (
                  <Button key={option} className={TOGGLE_BUTTON}>
                    {capitalizeFirstLetter(option)}
                  </Button>
                ))}
              </Toggle>
            </div>
          </Collapse.Panel>
        </Collapse>
      </Collapse.Group>
    </div>
  );
};
